import React from "react";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { RefreshCw, Pencil, LogOut, Download } from "lucide-react";
import { TryadLogo } from "@/components/TryadLogo";
import { ExportDialog } from "@/components/ExportDialog";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

export function Header() {
  const { state, handleReset, setNameSheetOpen } = useApp();
  const { logout } = useAuth();
  const [confirmReset, setConfirmReset] = React.useState(false);
  const [confirmLogout, setConfirmLogout] = React.useState(false);
  const [exportOpen, setExportOpen] = React.useState(false);

  if (!state) return null;

  const doReset = () => {
    handleReset();
    setConfirmReset(false);
    toast.success("Progreso reiniciado");
  };

  const doLogout = () => {
    logout();
    setConfirmLogout(false);
  };

  return (
    <div className="flex items-center justify-between mb-7">
      {/* Left: logo + greeting */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div
          className="h-11 w-11 rounded-2xl flex items-center justify-center flex-shrink-0"
          style={{ background: "rgba(10,133,255,0.12)", border: "1px solid rgba(10,133,255,0.22)" }}
        >
          <TryadLogo size={22} className="text-primary" />
        </div>
        <button onClick={() => setNameSheetOpen("edit")} className="text-left group min-w-0">
          <p className="text-[10px] font-semibold text-primary/80 uppercase tracking-[0.18em] leading-none mb-0.5">
            TRYAD
          </p>
          <div className="flex items-center gap-1.5">
            <h1 className="text-[22px] font-bold text-foreground tracking-tight truncate leading-none">
              Hola, {state.userName || "Atleta"}
            </h1>
            <Pencil className="h-3 w-3 text-white/20 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
          </div>
        </button>
      </div>

      {/* Right: actions */}
      <div className="flex items-center gap-1.5 ml-3">
        <button
          onClick={() => setExportOpen(true)}
          aria-label="Exportar mis datos"
          className="h-9 w-9 rounded-full flex items-center justify-center text-white/30 hover:text-white/70 hover:bg-white/8 transition-all duration-200"
        >
          <Download className="h-4 w-4" />
        </button>
        <button
          onClick={() => setConfirmReset(true)}
          aria-label="Reiniciar progreso"
          className="h-9 w-9 rounded-full flex items-center justify-center text-white/30 hover:text-white/70 hover:bg-white/8 transition-all duration-200"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
        <button
          onClick={() => setConfirmLogout(true)}
          aria-label="Cerrar sesión"
          className="h-9 w-9 rounded-full flex items-center justify-center text-white/30 hover:text-white/70 hover:bg-white/8 transition-all duration-200"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>

      <ExportDialog open={exportOpen} onOpenChange={setExportOpen} />

      {/* Reset dialog */}
      <Dialog open={confirmReset} onOpenChange={setConfirmReset}>
        <DialogContent className="glass-surface rounded-3xl border-0 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-foreground">Reiniciar progreso</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Esto borrará todos los pesos y series marcadas. ¿Continuar?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row gap-2">
            <Button variant="ghost" onClick={() => setConfirmReset(false)}
              className="flex-1 rounded-xl glass-surface text-foreground/80 hover:text-foreground hover:bg-secondary/60">Cancelar</Button>
            <Button onClick={doReset}
              className="flex-1 rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90">Reiniciar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Logout dialog */}
      <Dialog open={confirmLogout} onOpenChange={setConfirmLogout}>
        <DialogContent className="glass-surface rounded-3xl border-0 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-foreground">Cerrar sesión</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              ¿Seguro que quieres salir de tu cuenta?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row gap-2">
            <Button variant="ghost" onClick={() => setConfirmLogout(false)}
              className="flex-1 rounded-xl glass-surface text-foreground/80 hover:text-foreground hover:bg-secondary/60">Cancelar</Button>
            <Button onClick={doLogout}
              className="flex-1 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90">Salir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
